import { createAppSlice } from "../../app/createAppSlice";
import type { AppThunk } from "../../app/store";
import { ng } from "@common/ngds";
import { getClientId } from "../../App";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { Project } from "../../ProjectExplorer";
export const getUser = createAction<number>("api/users");
import { current } from "@reduxjs/toolkit";
import i18n from "../../../i18n";
import { addAlert } from "../ui/uiSlice";
import { useEffect } from "react";
import type { RootState } from "../../app/store";

export interface User {
  id: number;
  email: string;
  name: string;
  picture: string;
  projectdigest: Record<string, Project>;
}

const NOT_SIGNED_IN = "User not signed in";
const NO_LICENCE_ =
  "You are missing a valid license. Either no commercial license has been added to your user, or in the case of trial license it could have expired - contact StruSoft for help";
const LOGIN_ERROR_ = "Error during login, please try again or contact StruSoft.";
const LOGIN_DB_ERROR_ = "Database error during login, please try again or contact StruSoft.";
const ALL_LICENSES_IN_USE_ =
  "All licenses are currently in use by other users or active sessions. Please try again later or contact Strusoft for assistance.";

export const checkAccess = createAsyncThunk("users/checkAccess", async (args: { token: string }, thunkAPI) => {
  const tempheaders = {
    authorization: args.token ? `Bearer ${args.token}` : "",
    "X-Client-ID": getClientId(),
  };

  const resp = await fetch(`api/check/all`, { headers: tempheaders });

  if (!resp.ok) {
    thunkAPI.dispatch(
      addAlert({
        id: crypto.randomUUID(), // eller egen ID-generator
        type: "danger",
        text: "Failed to get user data",
        status: "danger",
      }),
    );

    console.error(`API Error: ${resp.status} ${resp.statusText}`);

    return thunkAPI.rejectWithValue({
      status: resp.status,
      statusText: resp.statusText,
    });
  }
  return await resp.json();
});

export const fetchUser = createAsyncThunk(
  "users/user",
  async (args: { token: string }, thunkAPI) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": getClientId(),
    };

    const resp = await fetch(`api/users/`, { headers: tempheaders });
    if (!resp.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to get user data",
          status: "danger",
        }),
      );

      console.error(`API Error: ${resp.status} ${resp.statusText}`);
      return thunkAPI.rejectWithValue({
        status: resp.status,
        statusText: resp.statusText,
      });
    }

    const data = await resp.json();

    if (data.userLicenseStatus.beam === 409 || data.userLicenseStatus.foundation === 409) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: ALL_LICENSES_IN_USE_,
          status: "danger",
        }),
      );
    }

    let lang = data.user?.settings?.lang ?? "en";
    if (localStorage.getItem("i18nextLng") === null) {
      localStorage.setItem("i18nextLng", lang);
      i18n.changeLanguage(lang);
    }
    return data;
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as RootState;
      return state.UserSlice.status !== "loading";
    },
  },
);

export const logoutUser = createAsyncThunk("users/logout", async (args: { token: string }, thunkAPI) => {
  const tempheaders = {
    authorization: args.token ? `Bearer ${args.token}` : "",
    "X-Client-ID": getClientId(),
  };

  const resp = await fetch(`api/users/logout`, { headers: tempheaders });
  if (!resp.ok) {
  }
  const data = await resp.json();
  return data;
});

export const getTokenFromAuth0 = createAsyncThunk(
  "users/getTokenFromAuth0",
  async (args: { getAccessToken: any; cache: boolean }, thunkAPI) => {
    try {
      const accessToken = await args.getAccessToken({
        cacheMode: args.cache ? "on" : "off",
        authorizationParams: {
          scope: "openid profile email read:current_user",
          ignoreCache: args.cache == false,
        },
      });
      return accessToken;
    } catch (e) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Error now getting access ",
          status: "danger",
        }),
      );
      console.error("Error getting access token:", e);
    }
  },
);

export function useUser() {
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    async function fetchData() {
      try {
        if (!isLoading && isAuthenticated) {
          await getUser();
        }
      } catch (e) {
        console.log(e);
      }
    }
    fetchData();
  }, [isLoading, isAuthenticated]);

  // Re-fetch when the tab becomes visible (e.g. user switches back from another tab)
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        getUser().catch(console.log);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isAuthenticated]);

  // useEffect(() => {
  //   if (!isAuthenticated) return;

  //   const isMetadataEditActive = () => {
  //     const activeElement = document.activeElement as HTMLElement | null;
  //     if (!activeElement) return false;

  //     if (activeElement.matches("input, textarea, select, [contenteditable='true']")) {
  //       return true;
  //     }

  //     return activeElement.closest(".editfield-container") !== null;
  //   };

  //   const intervalId = setInterval(() => {
  //     // Avoid replacing local in-progress edits while the user is typing in form fields.
  //     if (document.visibilityState !== "visible" || isMetadataEditActive()) {
  //       return;
  //     }

  //     getUser().catch(console.log);
  //   }, 30_000);

  //   return () => clearInterval(intervalId);
  // }, [isAuthenticated]);

  const getUser = async () => {
    let token = await getAccessTokenSilently();
    await dispatch(await fetchUser({ token: token }));
  };

  const logoutSimplexUser = async () => {
    let token = await getAccessTokenSilently();
    await dispatch(await logoutUser({ token: token }));
  };

  const checkAccessUser = async () => {
    let token = await getAccessTokenSilently();
    await dispatch(await checkAccess({ token: token }));
  };

  return { getUser, logoutSimplexUser, checkAccessUser };
}

export type userState = {
  user: any | null;
  token: string | null;
  status: "idle" | "loading" | "failed";
  access: Record<string, number>;
};

const initialState: userState = {
  user: null,
  status: "idle",
  token: null,
  access: {},
};

export const UserSlice = createAppSlice({
  name: "UserSlice",
  initialState: initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = { ...action.payload };
    },
    clearUser: (state) => {
      state.user = null;
    },
    // Local-only profile edits (name/signature shown in the offcanvas menu). There is no backend
    // endpoint yet to persist these, so they live in Redux state for the session only.
    updateUserProfile: (state, action: { payload: { name?: string; signature?: string } }) => {
      if (!state.user) return;
      if (action.payload.name !== undefined) {
        state.user.name = action.payload.name;
      }
      if (action.payload.signature !== undefined) {
        state.user.signature = action.payload.signature;
      }
    },
    updateFile: (state, action) => {
      let st = current(state);
      const fileIndex = st.user.projectdigest[action.payload.selectedProject].files[action.payload.index]
        ? action.payload.index
        : -1;
      if (fileIndex !== -1) {
        if (action.payload.field !== "keywords") {
          state.user.projectdigest[action.payload.selectedProject].files[fileIndex][action.payload.field] =
            action.payload.value;
        } else {
          // Accept either an array directly or a comma-separated string
          const val = action.payload.value;
          state.user.projectdigest[action.payload.selectedProject].files[fileIndex][action.payload.field] =
            Array.isArray(val) ? val : val.split(",").map((keyword: string) => keyword.trim());
        }
      } else {
        console.warn("File not found in user files");
      }
    },
    resetFile: (state, action) => {
      let st = current(state);
      const { selectedProjectName, index } = action.payload;
      if (st.user.projectdigest[selectedProjectName]) {
        const file = st.user.projectdigest[selectedProjectName].files[index];
        if (file) {
          // Reset the file to its initial state
          state.user.projectdigest[selectedProjectName].files[index] = {
            ...action.payload.file,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUser.pending, (state) => {
      state.status = "loading";
      console.log(" user pending");
    });

    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.status = "idle";
      if (action.payload.status === "no access general") {
        const emptyPayload = {};
        let obj = ng.frontend.beam.Project.fromObject(emptyPayload);
        state.user = {} as ng.frontend.beam.Project_json;
      } else {
        state.user = { ...action.payload };
        // Cached purely so the next load's skeleton can guess how many rows to show before this
        // request resolves (see PROJECT_COUNT_CACHE_KEY in ProjectExplorerSkeleton.tsx) — not used
        // for anything else, and safe to ignore/go stale, since it's just a visual placeholder count.
        try {
          const count = Object.keys(action.payload.projectdigest ?? {}).length;
          localStorage.setItem("sx-center:lastProjectCount", String(count));
        } catch {
          // localStorage unavailable (e.g. private browsing) — skeleton falls back to its default.
        }
      }
    });

    builder.addCase(fetchUser.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(getTokenFromAuth0.pending, () => {});

    builder.addCase(getTokenFromAuth0.fulfilled, (state, action: any) => {
      state.token = action.payload;
    });

    builder.addCase(checkAccess.fulfilled, (state, action) => {
      state.access = action.payload;
    });
  },
  selectors: {
    selectUser: (state) => state.user,
    selectToken: (state) => state.token,
    selectAccess: (state) => state.access,
    selectIsSalesUser: (state) => (state.user?.roles as string[] | undefined)?.includes("sales") ?? false,
  },
});

export const { selectUser, selectToken, selectAccess, selectIsSalesUser } = UserSlice.selectors;
export const { updateFile, resetFile, updateUserProfile } = UserSlice.actions;
